import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Cpu, Activity, Plug, Radio, Plus, Menu as MenuIcon, ChevronDown, Settings, LogOut, Check, Coins } from "lucide-react";
import { MusicPlayer } from "@/components/MusicPlayer";
import { NotificationBell } from "@/components/NotificationBell";

const GROUPS = [
  { label: "Command", items: [["world", "HQ City"], ["boss", "AI Boss"]] },
  { label: "Comms", items: [["messages", "Messages"], ["groups", "Groups"], ["email", "Email"], ["life", "Life Inbox"]] },
  { label: "Business", items: [["marketing", "Studio"], ["leads", "Lead Finder"], ["store", "Domains"], ["billing", "Billing & Store"], ["deliverables", "Deliverables"], ["approvals", "Approvals"], ["automations", "Automations"]] },
  { label: "Ops", items: [["scoreboard", "Scoreboard"], ["departments", "Departments"], ["logs", "Logs"], ["connections", "Connections"]] },
];
const LABELS = Object.fromEntries(GROUPS.flatMap((g) => g.items));

function Metric({ icon: Icon, label, value, color }) {
  return (
    <div className="hidden xl:flex items-center gap-2 px-3 py-1 rounded-lg bg-white/[0.03] border border-white/5">
      <Icon size={14} style={{ color }} />
      <div className="leading-none">
        <div className="font-mono text-sm font-semibold" style={{ color }}>{value}</div>
        <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      </div>
    </div>
  );
}

export default function HudHeader({ agentCount, activeCount, connectedCount, feedCount, mode, setMode, onHire, user, onUpgrade, onLogout }) {
  const [menu, setMenu] = useState(false);
  const [profile, setProfile] = useState(false);
  const go = (key) => { setMode(key); setMenu(false); };

  return (
    <header data-testid="app-header" className="fixed top-0 inset-x-0 z-40 h-16 backdrop-blur-xl bg-[#070a0f]/90 border-b border-white/10 flex items-center px-3 sm:px-5 gap-3">
      {/* Logo → home */}
      <button data-testid="logo-home" onClick={() => setMode("world")} className="flex items-center gap-2 shrink-0 group" title="Home — HQ City">
        <div className="relative w-9 h-9 rounded-lg grid place-items-center bg-cyan-500/10 border border-cyan-400/40 group-hover:border-cyan-300 transition-colors">
          <div className="absolute w-2 h-2 rounded-full bg-cyan-400 anim-flicker" style={{ boxShadow: "0 0 10px #00f0ff" }} />
          <span className="font-display font-black text-cyan-300 text-xs">OS</span>
        </div>
        <div className="leading-none text-left">
          <div className="font-display font-black tracking-widest text-base sm:text-lg text-zinc-100">LIFE<span className="text-cyan-400">//</span>OS</div>
          <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 hidden sm:block">Visual AI Workforce</div>
        </div>
      </button>

      {/* Menu dropdown (works on all sizes, replaces cramped tabs) */}
      <div className="relative ml-1 sm:ml-3">
        <button data-testid="nav-menu-toggle" onClick={() => { setMenu((v) => !v); setProfile(false); }} aria-expanded={menu}
          className="flex items-center gap-2 h-9 px-3 rounded-xl bg-white/[0.04] border border-white/10 hover:border-cyan-400/40 text-zinc-200 transition-colors">
          <MenuIcon size={15} className="text-cyan-300" />
          <span className="font-tactical font-semibold uppercase tracking-wider text-xs text-cyan-200">{LABELS[mode] || "Menu"}</span>
          <ChevronDown size={14} className={`text-zinc-500 transition-transform ${menu ? "rotate-180" : ""}`} />
        </button>
        {menu && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setMenu(false)} />
            <div data-testid="nav-menu" className="absolute left-0 top-12 z-50 w-[300px] max-h-[75vh] overflow-y-auto thin-scroll rounded-2xl bg-[#060a11] backdrop-blur-2xl border border-cyan-400/30 shadow-[0_24px_70px_rgba(0,0,0,0.85)] ring-1 ring-black/40 p-2">
              {GROUPS.map((grp) => (
                <div key={grp.label} className="mb-1.5 last:mb-0">
                  <div className="px-2 py-1 text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-400">{grp.label}</div>
                  <div className="grid grid-cols-2 gap-1">
                    {grp.items.map(([key, label]) => (
                      <button key={key} data-testid={`mode-${key}`} onClick={() => go(key)}
                        className={`flex items-center justify-between px-2.5 py-2 rounded-lg text-[12px] font-tactical font-semibold text-left transition-all duration-150 ${mode === key ? "bg-cyan-500/20 text-cyan-200 border border-cyan-400/40 shadow-[0_0_12px_rgba(0,240,255,0.15)]" : "text-zinc-300 bg-white/[0.02] border border-white/5 hover:bg-cyan-400/15 hover:text-white hover:border-cyan-400/40"}`}>
                        <span className="truncate">{label}</span>
                        {mode === key && <Check size={12} className="text-cyan-300 shrink-0" />}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="hidden xl:flex items-center gap-2 ml-2">
        <Metric icon={Radio} label="Workers" value={agentCount} color="#00f0ff" />
        <Metric icon={Activity} label="Active Now" value={activeCount} color="#36f08a" />
        <Metric icon={Plug} label="Connected" value={connectedCount} color="#ffb454" />
        <Metric icon={Cpu} label="Events" value={feedCount} color="#a78bfa" />
      </div>

      <div className="ml-auto flex items-center gap-2">
        <MusicPlayer />
        <NotificationBell onOpen={() => setMode("messages")} />
        {user && (
          <button data-testid="credits-pill" onClick={() => setMode("billing")} title="Credit wallet & store"
            className="flex items-center gap-1.5 h-9 px-3 rounded-xl bg-cyan-500/10 border border-cyan-400/30 hover:border-cyan-300/60 hover:bg-cyan-500/20 transition-colors">
            <Coins size={15} className="text-cyan-300" />
            <span className="font-mono text-sm font-semibold text-cyan-200">{user.credits ?? 0}</span>
            <span className="hidden sm:inline text-[9px] uppercase tracking-[0.18em] text-cyan-400/70">credits</span>
          </button>
        )}
        <Button data-testid="hire-agent-modal-trigger" onClick={onHire}
          className="bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase tracking-wider text-xs sm:text-sm rounded-xl px-3 sm:px-4 h-9 shadow-[0_0_20px_rgba(0,240,255,0.35)] transition-all hover:shadow-[0_0_28px_rgba(0,240,255,0.5)]">
          <Plus size={16} className="mr-1" /> <span className="hidden sm:inline">Hire</span> Agent
        </Button>

        {/* Profile menu */}
        {user && (
          <div className="relative">
            <button data-testid="profile-toggle" onClick={() => { setProfile((v) => !v); setMenu(false); }} aria-expanded={profile}
              className="w-9 h-9 grid place-items-center rounded-full bg-cyan-500/15 border border-cyan-400/40 text-cyan-300 font-display font-bold text-xs hover:bg-cyan-500/25 transition-colors" title={user.email}>
              {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
            </button>
            {profile && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setProfile(false)} />
                <div data-testid="profile-menu" className="absolute right-0 top-12 z-50 w-64 rounded-2xl bg-[#060a11] backdrop-blur-2xl border border-cyan-400/30 shadow-[0_24px_70px_rgba(0,0,0,0.85)] ring-1 ring-black/40 p-2">
                  <div className="px-3 py-2 border-b border-white/10 mb-1">
                    <div className="text-sm font-semibold text-zinc-100 truncate">{user.name || "Your account"}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{user.email}</div>
                  </div>
                  <button data-testid="profile-plan" onClick={() => { setMode("billing"); setProfile(false); }} className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-[13px] text-zinc-200 hover:bg-white/5">
                    <span className="flex items-center gap-2"><Coins size={14} className="text-cyan-300" /> Credits &amp; billing</span>
                    <span className="font-mono text-[11px] text-cyan-300">{user.credits ?? 0}</span>
                  </button>
                  <button data-testid="profile-settings" onClick={() => { setMode("connections"); setProfile(false); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] text-zinc-200 hover:bg-white/5">
                    <Settings size={14} className="text-zinc-400" /> Connections & settings
                  </button>
                  <button data-testid="profile-logout" onClick={() => { onLogout?.(); setProfile(false); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] text-rose-300 hover:bg-rose-500/10">
                    <LogOut size={14} /> Sign out
                  </button>
                  <div className="px-3 pt-1 text-[9px] font-mono uppercase tracking-widest text-zinc-600">Plan · {user.plan}</div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
